import { useState, useEffect } from "react";
import { apiRequest } from "../../../network/apiClient";
import { DropdownReminderIcon } from "/src/components/lead-inventory/icons";
import { createPortal } from "react-dom";
import { API_ENDPOINTS } from "../../../api/apiEndpoints";
import { toast } from "react-toastify";

const AddReminderButton = ({leadId, refetch, section = "Account" }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    details: "",
    datetime: "", 
    leadId: leadId || "", // Ensure it's not null initially
    section: section
  });

  // 🔹 Helper: current local datetime in YYYY-MM-DDTHH:MM (for datetime-local)
  const getLocalNow = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Update formData when leadId or section changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      leadId: leadId || "",
      section: section
    }));
  }, [leadId, section]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 🔒 Prevent backdated datetime on change
    if (name === "datetime") {
      const nowLocal = getLocalNow();
      if (value && value < nowLocal) {
        toast.error("Reminder time cannot be in the past.");
        return;
      }
    }

    setFormData({ ...formData, [name]: value });
  };

  const addReminder = async () => {
    const nowLocal = getLocalNow();

    // 🔒 Final guard before calling API
    if (!formData.datetime) {
      toast.error("Please select a reminder date & time.");
      return;
    }

    if (formData.datetime < nowLocal) {
      toast.error("Reminder time cannot be in the past.");
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiRequest(
        `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.ADD_REMINDER}`,
        "POST",
        formData
      );
      if (refetch) {
        refetch();
      }
      toast.success("Reminder Successfully Added");
      closeModal();
    } catch (error) {
      toast.error(error || "Reminder save unsuccessful");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative group">
      {/* Button to open the modal */}
      <button
        onClick={openModal}
        className="text-[#1E293B] block px-4 py-2 text-sm hover:bg-[#F1F5F9] text-center w-full"
      >
        <img
          src={DropdownReminderIcon}
          alt="Reminder"
          className="inline-block w-6 h-6 mr-2"
        />{" "}
        Add Reminder
      </button>

      {/* Modal */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-1/3">
              <div className="flex justify-between items-start w-full border-b border-gray-300 mr-5 mb-4">
                <h2 className="text-lg font-bold mb-2">Add a Reminder</h2>
              </div>
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={formData.title}
                onChange={handleChange}
                className="w-full p-2 mb-3 border rounded"
              />
              <textarea
                name="details"
                placeholder="Details"
                value={formData.details}
                onChange={handleChange}
                className="w-full p-2 mb-3 border rounded"
              />
              <input
                type="datetime-local"
                name="datetime"
                value={formData.datetime}
                onChange={handleChange}
                className="w-full p-2 mb-3 border rounded"
                min={getLocalNow()}  // 🔒 cannot pick past date/time
              />

              <div className="flex justify-end space-x-2">
                <button
                  onClick={closeModal}
                  className="px-4 py-3 bg-white border-[1px] border-black rounded-3xl hover:bg-gray-100"
                >
                  <b>Cancel</b>
                </button>
                <button
                  onClick={addReminder}
                  className={`rounded-3xl px-8 py-3 text-black ${
                    isLoading ? "bg-gray-500" : "bg-yellow-300 hover:bg-yellow-400"
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? "Adding..." : <b>Submit</b>}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AddReminderButton;
